/// <amd-module name="Build/Common/Library/Components/CommitLink" />
import * as React from "react";
import * as Resources from "Build/Common/Library/Resources";

import { ISourceProviderService } from 'Build/SourceProviders/SourceProvider';

import { ICancelReason } from "VSS/Core/Util/Promise";
import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";

// repositoryId is used instead of repositoryName in some source providers
// requiring both here so that those who consume this control are most likely to get a valid link
export interface ICommitLinkProps {
    // default: true
    excludeTabStop?: boolean;
    repositoryType: string;
    repositoryName: string;
    repositoryId: string;
    commitId: string;
    monospace: boolean;
    readonly?: boolean;
    projectNamePromise?: Promise<string | null>;
    showIcon?: boolean; // default true
}

interface ICommitLinkState {
    commitLink: string;
    commitText: string;
}

export class CommitLink extends VssComponent<ICommitLinkProps, ICommitLinkState> {
    constructor(props: ICommitLinkProps, context: IVssComponentContext) {
        super(props, context);

        const sourceProviderService = this.context.pageContext.getService<ISourceProviderService>("ISourceProviderService");
        const sourceProvider = sourceProviderService.getSourceProvider(this.props.repositoryType);
        const commitText = sourceProvider ? sourceProvider.getCommitText(this.props.commitId) : "";
        
        this.state = {
            commitLink: "#",
            commitText
        };
    }

    public componentDidMount() {
        super.componentDidMount();
        this._refreshStateData();
    }

    public componentDidUpdate(prevProps: ICommitLinkProps, prevState: ICommitLinkState) {
        super.componentDidUpdate(prevProps, prevState);
        if (prevProps.repositoryId !== this.props.repositoryId || prevProps.commitId !== this.props.commitId) {
            this._refreshStateData();
        }
    }

    public render() {
        const { excludeTabStop = true, monospace, readonly } = this.props;
        let className = "scroll-hidden flex-row flex-baseline";
        if (monospace) {
            className += " monospaced-text";
        }

        if (!readonly && this.state.commitLink && this.state.commitText) {
            return (
                <FPSLink className={className} excludeTabStop={excludeTabStop} href={this.state.commitLink} subtle={true} ariaLabel={format(Resources.CommitLabelFormat, this.state.commitText)} removeUnderline={true}>
                    <span className="text-ellipsis">
                        {this.props.showIcon !== false && <Icon iconName={"BranchCommit"} className="pipelines-icon-right-margin" /> }
                        {this.state.commitText}
                    </span>
                </FPSLink>
            );
        }
        else if (this.state.commitText) {
            className += " bolt-table-link bolt-table-inline-link pipeline-link";
            return (
                <div className={className}>
                    <span className="text-ellipsis" aria-label={format(Resources.CommitLabelFormat, this.state.commitText)}>
                        {this.props.showIcon !== false && <Icon iconName={"BranchCommit"} className="pipelines-icon-right-margin" /> }
                        {this.state.commitText}
                    </span>
                </div>
            );
        }

        return <span></span>;
    }

    private _refreshStateData() {
        const sourceProviderService = this.context.pageContext.getService<ISourceProviderService>("ISourceProviderService");
        const sourceProvider = sourceProviderService.getSourceProvider(this.props.repositoryType);
        const commitText = sourceProvider ? sourceProvider.getCommitText(this.props.commitId) : "";
        if (!this.props.readonly && sourceProvider) {
            this.trackPromise(
                this.props.projectNamePromise || sourceProvider.getProjectName(this.props.repositoryId)
            ).promise.then((projectName: string | null) => 
                this.trackPromise(
                    sourceProvider.fetchCommitLink({
                        repositoryName: this.props.repositoryName || this.props.repositoryId,
                        commitId: this.props.commitId,
                        displayUri: ""
                    }, projectName ? projectName : undefined)
                ).promise
            ).then(
                (link: string) => {
                    this.setState({
                        commitLink: link,
                        commitText
                    });
                },
                (error: ICancelReason) => {
                    if (!error.isCanceled) {
                        console.log(error);
                    }
                }
            );
        }
        else {
            this.setState({
                commitLink: "#",
                commitText
            });
        }
    }
}
